<template>
  <VaCard class="limited-height-card">
    <VaCardTitle class="separate-title">
      <h3 class="mb-3 font-bold text-lg" style="text-transform: none">
        Viajes por {{ selectedPeriod === 'Diario' ? 'día' : 'mes' }}
      </h3>

      <VaButtonToggle v-model="selectedPeriod" :options="periods" color="background-element" size="small" />
    </VaCardTitle>
    <VaCardContent class="content-container">
      <section class="flex gap-x-10">
        <div class="data-container-naves">
          <div class="">
            Promedio {{ selectedPeriod === 'Diario' ? 'diario' : 'mensual' }} de viajes
            <div class="text-xl font-bold mb-3">
              {{ selectedPeriod === 'Diario' ? promedioDia.toFixed(2) : promedioMes.toFixed(2) }}
            </div>
          </div>
          <span class="text-secondary">
            <p class="text-danger">
              <span :class="changeClass">
                <template v-if="up">
                  <VaIcon name="arrow_upward" />
                </template>
                <template v-else>
                  <VaIcon name="arrow_downward" />
                </template>
                {{ selectedPeriod === 'Diario' ? changePercentageDia.toFixed(2) : changePercentageMes.toFixed(2) }}
              </span>
              <span class="text-secondary">
                del promedio
                {{ selectedPeriod === 'Diario' ? lastValueDia.toFixed(2) : lastValueMes.toFixed(2) }}
                del {{ selectedPeriod === 'Diario' ? 'mes' : 'año' }} pasado</span
              >
            </p>
          </span>
          <p class="text-xs text-danger mt-2 mb-2">
            <span class="text-secondary"> Calculado desde el </span>
            <span class="text-secondary font-bold">
              {{ selectedPeriod === 'Diario' ? toDayString(yearStartDate) : toDayString(monthStartDate) }}
            </span>
            <span class="text-secondary"> hasta el </span>
            <span class="text-secondary font-bold">
              {{ toDayString(currentDate) }}
            </span>
          </p>
        </div>
      </section>
      <div class="chart-wrapper mb-2">
        <VaChart v-if="selectedPeriod === 'Diario'" :data="chartDataDia" type="bar" :options="options" />
        <VaChart v-if="selectedPeriod === 'Mensual'" :data="chartDataMes" type="bar" :options="options" />
      </div>
    </VaCardContent>
  </VaCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, Ref, watch } from 'vue'
import dataStorageManager, { BucketDefaultIDs } from '../../../managers/data/DataStorageManager'
import { useComplexBucketList } from '../../../managers/data/utilities/complexBucketListController'
import { IRequest, RequestObjectType } from '../../../services/api-services/RequestsServices'
import { ITripulante } from '../../../services/api-services/TripulantesServices'
import {
  IViaje,
  OperatedViaje,
  requestedTripulante,
  ViajeCancelado,
  ViajeEstado,
} from '../../../services/api-services/ViajesServices'
import { IEmbarcacion } from '../../../services/api-services/EmbarcacionesServices'
import { v4 as uuid } from 'uuid'

import { VaCard } from 'vuestic-ui'
import VaChart from '../../../components/va-charts/VaChart.vue'
import { getChartData } from '../../../data/charts/composables/useChartData'
import { ChartOptions } from 'chart.js'
import { TBarChartData, TLineChartData } from '../../../data/types'

const periods = ['Diario', 'Mensual'].map((period) => ({ label: period, value: period }))
const selectedPeriod = ref('Diario') as Ref<'Diario' | 'Mensual'>

const dayLabels: Ref<string[]> = ref([])
const dayEventos: Ref<{ dia: Date; texto: string; events: number }[]> = ref([])
const monthLabels: Ref<string[]> = ref([])
const monthEventos: Ref<{ mes: Date; texto: string; events: number }[]> = ref([])

const requestsList: Ref<IRequest[]> = ref([])
const asociationsRequests: Ref<IRequest[]> = ref([])
const posicionamientosRequests: Ref<IRequest[]> = ref([])
const permisosMuelleWesteRequests: Ref<IRequest[]> = ref([])

const up: Ref<boolean> = ref(true)

const changeClass = computed(() => {
  return up.value ? 'text-success' : 'text-red-600'
})

watch(selectedPeriod, () => {
  if (selectedPeriod.value === 'Diario') {
    if (promedioDia.value >= lastValueDia.value) {
      up.value = true
    } else {
      up.value = false
    }
  } else {
    if (promedioMes.value >= lastValueMes.value) {
      up.value = true
    } else {
      up.value = false
    }
  }
})

const showList: Ref<
  {
    viaje: OperatedViaje
    day: Date // Es el dia exacto del viaje
    month: Date // El primer dia del mes del viaje
  }[]
> = ref([])

const lineChartDataViajesDia: Ref<TBarChartData> = ref({
  labels: [],
  datasets: [
    {
      label: 'Viajes',
      borderColor: '#007cc3',
      backgroundColor: '#007cc3',
      data: [],
    },
  ],
})

const lineChartDataViajesMes: Ref<TBarChartData> = ref({
  labels: [],
  datasets: [
    {
      label: 'Viajes',
      borderColor: '#007cc3',
      backgroundColor: '#007cc3',
      data: [],
    },
  ],
})

const chartDataDia: Ref<TBarChartData> = computed(() => getChartData(lineChartDataViajesDia.value))
const chartDataMes: Ref<TBarChartData> = computed(() => getChartData(lineChartDataViajesMes.value))

const options: ChartOptions<'bar'> = {
  scales: {
    x: {
      display: true,
      grid: {
        display: false, // Disable X-axis grid lines ("net")
      },
    },
    y: {
      display: true,
      grid: {
        display: false, // Disable Y-axis grid lines ("net")
      },
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
    },
  },
}

const currentMes = new Date().getMonth()
const currentYear = new Date().getFullYear()
const currentDay = new Date().getDate()
const previousMes = currentMes === 0 ? 11 : currentMes - 1
const previousYear = currentMes === 0 ? currentYear - 1 : currentYear
// Obtener ultimo dia del mes anterior (29,30,31)

const yearStartDate = new Date(currentYear, 0, 1)
const monthStartDate = new Date(currentYear, currentMes, 1)
const currentDate = new Date(currentYear, currentMes, currentDay)
// Formatear fecha a DD-MM-YYYY
const toDayString = (date: Date) => {
  const day = date.getDate()
  const fday = day < 10 ? `0${day}` : day
  const month = date.getMonth() + 1
  const fmonth = month < 10 ? `0${month}` : month
  const year = date.getFullYear()
  return `${fday}-${fmonth}-${year}`
}

const promedioMes = computed(() => {
  // Filtrar viajes de este año y calcular promedio mensual por meses pasados
  const isCurrentYear = (label: string) => {
    const [monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    return year.toString() === currentYear.toString()
  }
  const data = lineChartDataViajesMes.value
  const filteredLabels = data.labels!.filter((label) => isCurrentYear(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return filteredLabels.length > 0 ? total / (currentMes + 1) : 0
})
const promedioDia = computed(() => {
  const isCurrentMonth = (label: string) => {
    const [dayStr, monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return year.toString() === currentYear.toString() && month.toString() === currentMes.toString()
  }
  const data = lineChartDataViajesDia.value
  const filteredLabels = data.labels!.filter((label) => isCurrentMonth(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return filteredLabels.length > 0 ? total / currentDay : 0
})
const currentMesValue = computed(() => {
  // Filtrar viajes de este año y calcular promedio mensual por meses pasados
  const isCurrentMonth = (label: string) => {
    const [monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return year.toString() === currentYear.toString() && month.toString() === currentMes.toString()
  }
  const data = lineChartDataViajesMes.value
  const filteredLabels = data.labels!.filter((label) => isCurrentMonth(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total
})
const lastValueMes = computed(() => {
  const isPreviousYear = (label: string) => {
    const [monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    return year.toString() === (currentYear - 1).toString()
  }
  const data = lineChartDataViajesMes.value
  const filteredLabels = data.labels!.filter((label) => isPreviousYear(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total / 12
})
const changePercentageMes = computed(() => {
  const promedio = promedioMes.value
  const lastValue = lastValueMes.value
  if (promedio >= lastValue) {
    up.value = true
  } else {
    up.value = false
  }
  return Math.abs(lastValue - promedio)
})
const currentDiaValue = computed(() => {
  const isCurrentDay = (label: string) => {
    const [dayStr, monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    const day = parseInt(dayStr, 10)
    return (
      year.toString() === currentYear.toString() &&
      month.toString() === currentMes.toString() &&
      day.toString() === currentDay.toString()
    )
  }
  const data = lineChartDataViajesDia.value
  const filteredLabels = data.labels!.filter((label) => isCurrentDay(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total
})
const lastValueDia = computed(() => {
  const isPreviousMonth = (label: string) => {
    const [dayStr, monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return year.toString() === previousYear.toString() && month.toString() === previousMes.toString()
  }
  const data = lineChartDataViajesDia.value
  const filteredLabels = data.labels!.filter((label) => isPreviousMonth(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total / 30
})
const changePercentageDia = computed(() => {
  const promedio = promedioDia.value
  const lastPromedio = lastValueDia.value
  if (promedio >= lastPromedio) {
    up.value = true
  } else {
    up.value = false
  }
  return Math.abs(lastPromedio - promedio)
})

const { fullList, reload } = useComplexBucketList<OperatedViaje, IViaje>(BucketDefaultIDs.VIAJES, (item) => {
  const request: IRequest | undefined = requestsList.value
    .filter((req) => req.tipo_objeto === RequestObjectType.PatronViaje)
    .filter((req) => req.id_objeto.toString() === item.id.toString())
    .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
  const asociations: IRequest[] = asociationsRequests.value
    .filter((req) => req.tipo_objeto === RequestObjectType.Preacreditacion)
    .filter((req) => req.id_objeto.toString() === item.id.toString())
  const posicionamientos: IRequest[] = posicionamientosRequests.value
    .filter((req) => req.tipo_objeto === RequestObjectType.PosicionamientoViaje)
    .filter((req) => req.id_objeto.toString() === item.id.toString())
  const embarcacionData: IEmbarcacion | undefined = dataStorageManager.searchDataInBucket(
    BucketDefaultIDs.EMBARCACIONES,
    (e) => e.id === item.embarcacion_id,
  )

  const permisoMuelleWesteData: IRequest | undefined = permisosMuelleWesteRequests.value
    .filter((req) => req.tipo_objeto === RequestObjectType.PermisoMuelleWeste)
    .filter((req) => req.id_objeto.toString() === item.id.toString())
    .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request

  const armadorRequested = asociations.find(
    (a) => a.tipo_objeto_2 === RequestObjectType.Armador && a.id_objeto_2?.toString() == item.id_armador?.toString(),
  )

  const patronData: ITripulante | undefined = dataStorageManager
    .getCurrentBucketData<ITripulante>(BucketDefaultIDs.TRIPULANTES)
    .find((t) => t.id.toString() === request?.id_objeto_2?.toString())
  let patronRequested: requestedTripulante | undefined
  if (patronData) {
    const patronAsociation = asociations.find(
      (a) => a.tipo_objeto_2 === RequestObjectType.Tripulante && a.id_objeto_2?.toString() == patronData?.id.toString(),
    )
    patronRequested = {
      ...patronData,
      requestData: patronAsociation,
    }
  }

  const tripulantesData: Array<requestedTripulante> = dataStorageManager
    .getCurrentBucketData<ITripulante>(BucketDefaultIDs.TRIPULANTES)
    .filter((t) => asociations.some((a) => a.id_objeto_2?.toString() == t.id.toString()))
    .filter((t) => t.id.toString() !== patronData?.id.toString())
    .map((t) => {
      const tripulanteAsociation = asociations.find(
        (a) => a.tipo_objeto_2 === RequestObjectType.Tripulante && a.id_objeto_2?.toString() == t.id.toString(),
      )
      return {
        ...t,
        requestData: tripulanteAsociation,
      }
    })
  if (typeof item.marca_salida == 'object') {
    item.marca_salida = null
  }
  if (item.marca_fecha_inicio) {
    item.marca_fecha_inicio = new Date(item.marca_fecha_inicio).getFullYear() <= 1980 ? null : item.marca_fecha_inicio
  }
  if (item.marca_fecha_fin) {
    item.marca_fecha_fin = new Date(item.marca_fecha_fin).getFullYear() <= 1980 ? null : item.marca_fecha_fin
  }
  if (item.marca_salida) {
    item.marca_salida = new Date(item.marca_salida).getFullYear() <= 1980 ? null : item.marca_salida
  }

  return {
    ...item,
    requestData: request,
    posicionamientosData: posicionamientos,
    asociationData: asociations,
    embarcacionData: embarcacionData,
    patronData: patronRequested,
    armadorData: armadorRequested,
    tripulantesData: tripulantesData,
    permisoMuelleWesteData: permisoMuelleWesteData,
  }
})

onMounted(() => {
  dataStorageManager.subscribeBucket(BucketDefaultIDs.EMBARCACIONES, () => {})
  dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, () => {})
  dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, onRequestsLoad)
})

watch(fullList, () => {
  const showListTemp: {
    viaje: OperatedViaje
    day: Date
    month: Date
  }[] = []
  fullList.value.forEach((viaje) => {
    if (viaje.posicionamientosData.length > 0 && viaje.estado !== null && !ViajeCancelado(viaje)) {
      const viajeDate = new Date(viaje.fecha_viaje)
      const day = new Date(viajeDate.getFullYear(), viajeDate.getMonth(), viajeDate.getDate())
      const month = new Date(viajeDate.getFullYear(), viajeDate.getMonth(), 1)
      showListTemp.push({
        viaje: viaje,
        day: day,
        month: month,
      })
    }
  })
  showList.value = showListTemp
})

watch(showList, () => {
  const daySplittedSet = new Set<{ dia: Date; cant: number }>()
  const monthSplittedSet = new Set<{ mes: Date; cant: number }>()
  showList.value.forEach((item) => {
    const dayEntry = Array.from(daySplittedSet).find((d) => d.dia.getTime() === item.day.getTime())
    if (dayEntry) {
      daySplittedSet.delete(dayEntry)
      daySplittedSet.add({ dia: dayEntry.dia, cant: dayEntry.cant + 1 })
    } else {
      daySplittedSet.add({ dia: item.day, cant: 1 })
    }
    const monthEntry = Array.from(monthSplittedSet).find((m) => m.mes.getTime() === item.month.getTime())
    if (monthEntry) {
      monthSplittedSet.delete(monthEntry)
      monthSplittedSet.add({ mes: monthEntry.mes, cant: monthEntry.cant + 1 })
    } else {
      monthSplittedSet.add({ mes: item.month, cant: 1 })
    }
  })

  // Si no hay datos para el dia o mes actual, agregarlo con 0 eventos
  const today = new Date()
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const todayDayEntry = Array.from(daySplittedSet).find((d) => d.dia.getTime() === todayDay.getTime())
  if (!todayDayEntry) {
    daySplittedSet.add({ dia: todayDay, cant: 0 })
  }
  const todayMonthEntry = Array.from(monthSplittedSet).find((m) => m.mes.getTime() === todayMonth.getTime())
  if (!todayMonthEntry) {
    monthSplittedSet.add({ mes: todayMonth, cant: 0 })
  }

  // Obtener ultimos 30 dias y 12 meses
  const daySetList = Array.from(daySplittedSet)
    .sort((a, b) => a.dia.getTime() - b.dia.getTime())
    .slice(-30)
  const monthSetList = Array.from(monthSplittedSet)
    .sort((a, b) => a.mes.getTime() - b.mes.getTime())
    .slice(-12)

  dayLabels.value = daySetList
    .sort((a, b) => a.dia.getTime() - b.dia.getTime())
    .map((d) => {
      const day = d.dia.getDate()
      const month = d.dia.getMonth() + 1
      const year = d.dia.getFullYear()
      return `${day}-${month}-${year}`
    })
  dayEventos.value = daySetList
    .sort((a, b) => a.dia.getTime() - b.dia.getTime())
    .map((d) => ({ dia: d.dia, texto: `${d.cant} viajes`, events: d.cant }))
  monthLabels.value = monthSetList
    .sort((a, b) => a.mes.getTime() - b.mes.getTime())
    .map((m) => {
      const month = m.mes.getMonth() + 1
      const year = m.mes.getFullYear()
      return `${month}-${year}`
    })
  monthEventos.value = monthSetList
    .sort((a, b) => a.mes.getTime() - b.mes.getTime())
    .map((m) => ({ mes: m.mes, texto: `${m.cant} viajes`, events: m.cant }))
})

watch(
  () => [dayLabels.value, dayEventos.value],
  () => {
    lineChartDataViajesDia.value = {
      labels: dayLabels.value,
      datasets: [
        {
          label: 'Viajes',
          borderColor: '#007cc3', //dayEventos.value.map(() => '#ff6384'),
          backgroundColor: '#007cc3', //dayEventos.value.map(() => '#ff6384'),
          data: dayEventos.value.map((e) => e.events),
        },
      ],
    }
  },
  { immediate: true },
)

watch(
  () => [monthLabels.value, monthEventos.value],
  () => {
    lineChartDataViajesMes.value = {
      labels: monthLabels.value,
      datasets: [
        {
          label: 'Viajes',
          borderColor: '#007cc3', //monthEventos.value.map(() => '#ff6384'),
          backgroundColor: '#007cc3', //monthEventos.value.map(() => '#ff6384'),
          data: monthEventos.value.map((e) => e.events),
        },
      ],
    }
  },
  { immediate: true },
)

function onRequestsLoad(requests: Array<IRequest>) {
  requestsList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PatronViaje)
  asociationsRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Preacreditacion)
  posicionamientosRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PosicionamientoViaje)
  permisosMuelleWesteRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PermisoMuelleWeste)
  reload.value = uuid()
}
</script>
<style scoped>
.va-card {
  /*max-height: 580px;*/
}

.separate-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.content-container {
  display: flex;
  flex-direction: column;
  height: calc(100% - 48px);
}

.chart-wrapper {
  position: relative;
  width: 100%;
  height: 220px;
  min-width: 0;
}
</style>
